import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Toggle list reconciled with what generateComplianceReport actually renders.
// site_overview/audit_trail/signatures were removed (they were no-ops); see
// wqugEUH0. Image embedding for the Photo Index is deferred to a follow-up
// (fix/audit-pack-embed-photos) — the toggle here controls the Photo Index
// reference table only.
const AUDIT_PACK_ITEMS = [
  { id: 'documents', label: 'All Documents & Certificates', icon: 'FileText', included: true },
  { id: 'incidents', label: 'Incident Register', icon: 'ShieldAlert', included: true },
  { id: 'near_misses', label: 'Near Misses', icon: 'AlertTriangle', included: true },
  { id: 'snags', label: 'Snag Log', icon: 'AlertCircle', included: true },
  { id: 'audits', label: 'Audit & Inspection Records', icon: 'ClipboardCheck', included: true },
  { id: 'diary', label: 'Site Diary Entries', icon: 'BookOpen', included: true },
  { id: 'rams', label: 'H&S Docs', icon: 'HardHat', included: true },
  { id: 'photos', label: 'Photo Index', icon: 'Camera', included: true },
];

const AuditPackExport = ({ onExport, isExporting, selectedSite, siteName, disabled = false }) => {
  const [packItems, setPackItems] = useState(AUDIT_PACK_ITEMS);

  const toggleItem = (id) => {
    setPackItems(prev => prev?.map(item =>
      item?.id === id ? { ...item, included: !item?.included } : item
    ));
  };

  const includedCount = packItems?.filter(i => i?.included)?.length;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <Icon name="Package" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white font-heading">Audit Pack Export</h2>
            <p className="text-slate-300 text-xs mt-0.5">One-click compliance package for auditors</p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Site indicator */}
        {siteName && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-accent/5 border border-accent/20 rounded-lg">
            <Icon name="Building2" size={14} className="text-accent" />
            <span className="text-sm font-medium text-foreground">{siteName}</span>
          </div>
        )}

        {!selectedSite && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Icon name="Info" size={14} className="text-amber-600" />
            <span className="text-xs text-amber-700">Select a site above to generate a site-specific audit pack</span>
          </div>
        )}

        {/* Pack contents */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-foreground">Pack Contents</p>
            <span className="text-xs text-muted-foreground">{includedCount}/{packItems?.length} included</span>
          </div>
          <div className="space-y-1.5">
            {packItems?.map(item => (
              <button
                key={item?.id}
                onClick={() => toggleItem(item?.id)}
                className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-all text-left ${
                  item?.included
                    ? 'border-accent/30 bg-accent/5' :'border-border bg-transparent opacity-50'
                }`}
              >
                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                  item?.included ? 'bg-accent text-white' : 'bg-muted'
                }`}>
                  {item?.included ? (
                    <Icon name="Check" size={11} color="white" />
                  ) : (
                    <Icon name="Minus" size={11} className="text-muted-foreground" />
                  )}
                </div>
                <Icon name={item?.icon} size={14} className={item?.included ? 'text-accent' : 'text-muted-foreground'} />
                <span className="text-xs font-medium text-foreground">{item?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Audit trail note */}
        <div className="p-3 bg-slate-50 rounded-lg mb-4">
          <div className="flex items-start gap-2">
            <Icon name="Shield" size={13} className="text-slate-500 mt-0.5" />
            <p className="text-xs text-slate-600 leading-relaxed">
              The Audit Pack includes a timestamped audit trail summary of all edits, uploads, and signatures — formatted for regulatory submission.
            </p>
          </div>
        </div>

        {/* Export button */}
        <Button
          variant="primary"
          className="w-full"
          onClick={() => onExport(packItems?.filter(i => i?.included)?.map(i => i?.id))}
          disabled={isExporting || includedCount === 0 || disabled}
        >
          {isExporting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              Generating Audit Pack...
            </>
          ) : (
            <>
              <Icon name="Download" size={16} className="mr-2" />
              Export Audit Pack ({includedCount} sections)
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AuditPackExport;
